import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GlobalSearchResults } from "@/utils/types/algolia";
import { fetchRecomendSearchResults } from "@/services/algolia";

interface AlgoliaSliceType {
  popularItems: null | GlobalSearchResults;
  popularItemsStore: null | string;
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
  pathQueue: string[];
  search: {
    queryValue: string | null;
    queryID: {
      products: string | null;
      recipes: string | null;
      articles: string | null;
    };
  };
}

const initialState: AlgoliaSliceType = {
  popularItems: null as GlobalSearchResults | null,
  popularItemsStore: null,
  loading: "idle",
  error: null,
  pathQueue: [],
  search: {
    queryValue: null,
    queryID: {
      products: null,
      recipes: null,
      articles: null,
    },
  },
};

export const getPopularSearchData = createAsyncThunk(
  "algolia/getPopularSearchData",
  async (
    {
      filters,
      storeID,
    }: {
      filters?: {
        product?: string;
        recipe?: string;
        article?: string;
      };
      storeID: string;
    },
    { rejectWithValue }
  ) => {
    if (!storeID) {
      return rejectWithValue("Missing required parameter: storeID");
    }
    try {
      const response = await fetchRecomendSearchResults({
        storeID,
        filter: filters,
      });
      if (!response) {
        return rejectWithValue("No data returned from recommendations API");
      }
      return response;
    } catch (error: any) {
      console.error("getPopularSearchData error:", error);
      return rejectWithValue(
        error?.message ??
          "Something went wrong while fetching popular search data"
      );
    }
  }
);

const algoliaSlice = createSlice({
  name: "algolia",
  initialState,
  reducers: {
    addPathToQueue(state, action: PayloadAction<string>) {
      if (action.payload) {
        const pathLength = state.pathQueue.length;
        if (
          pathLength >= 2 &&
          state.pathQueue[pathLength - 1] === action.payload
        )
          return;
        if (state.pathQueue.length > 5) {
          state.pathQueue.shift();
        }
        state.pathQueue.push(action.payload);
      }
    },
   
    setPopularItems(state, action: PayloadAction<GlobalSearchResults | null>) {
      state.popularItems = action.payload;
    },
    setPopularItemsStore(state, action: PayloadAction<string | null>) {
      state.popularItemsStore = action.payload;
    },
    setSearchQuery(state, action) {
      state.search = { ...state.search, ...action.payload };
    },
    clearSearchQuery(state) {
      state.search.queryValue = null;
      state.search.queryID = {
        products: null,
        recipes: null,
        articles: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPopularSearchData.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getPopularSearchData.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.popularItems = action.payload;
      })
      .addCase(getPopularSearchData.rejected, (state) => {
        state.loading = "failed";
      });
  },
});

export const {
  setPopularItems,
  setPopularItemsStore,
  setSearchQuery,
  clearSearchQuery,
  addPathToQueue,
} = algoliaSlice.actions;
export const algoliaReducer = algoliaSlice.reducer;
